import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { Card } from "@shared/ui/card";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertCircle, HelpCircle } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { usePendingBills } from "../../hooks";
import { BillCard } from "./BillCard";
import { InstallmentCard } from "./InstallmentCard";
import { LatePaymentPenalty } from "./LatePaymentPenalty";
import { LastPaymentDetailsCard } from "./PatiallyPaidCard ";
import { ServiceOrderCard } from "./ServiceOrderCard";
import { ServicePaymentCard } from "./ServicePaymentCard";

interface PaymentDetailsFormProps {
  form: UseFormReturn<any>;
  fields: FormField[];
  onFieldChange: (fieldName: string, value: any) => void;
  paymentType: string;
  selectedBills: string[];
  selectedInstallments: string[];
  selectedServiceOrders: string[];
  selectedServicePayments: string[];
  onBillToggle: (billId: string) => void;
  onInstallmentToggle: (installmentId: string) => void;
  onServiceOrderToggle: (serviceOrderId: string) => void;
  onServicePaymentToggle: (servicePaymentId: string) => void;
  lateFeesWaived: boolean;
  onWaiveLateFee: (waived: boolean) => void;
  onSelectAllBills?: (billIds: string[]) => void;
  currency: string;
  consumer?: { consumerId: string; consumerNo: string };
  installments?: any;
  serviceOrderPayments?: any;
  servicePayments?: any;
}

export const PaymentDetailsForm: React.FC<PaymentDetailsFormProps> = ({
  form,
  fields,
  onFieldChange,
  paymentType,
  selectedBills,
  selectedInstallments,
  selectedServiceOrders,
  selectedServicePayments,
  onBillToggle,
  onInstallmentToggle,
  onServiceOrderToggle,
  onServicePaymentToggle,
  lateFeesWaived,
  onWaiveLateFee,
  onSelectAllBills,
  currency,
  consumer,
  installments,
  serviceOrderPayments,
  servicePayments,
}) => {
  // Get entities based on payment type

  const { data: pendingBills } = usePendingBills({
    remoteUtilityId: getRemoteUtilityId(),
    consumerId: Number(consumer?.consumerId),
  });

  // Render entity cards based on payment type
  const renderEntityCards = () => {
    if (!paymentType) return null;

    switch (paymentType) {
      case "2": {
        const isMultiBill = selectedBills.length > 1;
        const totalPendingBills = pendingBills?.pendingBills || [];
        const allSelected =
          selectedBills.length > 0 &&
          selectedBills.length === totalPendingBills.length;

        const selectedOverdueBills = totalPendingBills.filter(
          (bill) =>
            selectedBills.includes(bill.invoiceNo) &&
            bill.billStatus?.toLowerCase() === "overdue"
        );
        const totalLateFee = selectedOverdueBills.reduce(
          (sum, b) => {
            const lateFeeVal = (b.lateFee != null && b.lateFee > 0) ? Number(b.lateFee) : ((Number(b.outstandingAmount) || 0) * 0.05);
            return sum + lateFeeVal;
          },
          0,
        );
        console.log("totalLateFee", totalLateFee);

        const selectedPartiallyPaidBills = totalPendingBills.filter(
          (bill) =>
            selectedBills.includes(bill.invoiceNo) &&
            bill.billPaymentStatus === "Partially Paid",
        );

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-1.5">
                <h4 className="text-[13px] font-bold text-gray-700">
                  Pending Bills
                </h4>
                <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
              </div>
              <div className="flex items-center gap-3 text-[12px]">
                <button
                  onClick={() =>
                    onSelectAllBills?.(
                      allSelected
                        ? []
                        : totalPendingBills.map((b) => b.invoiceNo),
                    )
                  }
                  className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {allSelected ? "Clear All" : "Select All"}
                </button>
                <span className="text-gray-500">
                  {selectedBills.length > 0
                    ? `${selectedBills.length} of `
                    : ""}
                  {totalPendingBills.length}{" "}
                  {selectedBills.length > 0 ? "selected" : "Total"}
                </span>
              </div>
            </div>

            {/* Multi-bill notice - below late fee or above amount (currently placed before bills, moving it) */}

            {/* Bills list */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {totalPendingBills.map((bill) => (
                <BillCard
                  key={bill.id}
                  bill={bill}
                  isSelected={selectedBills.includes(bill.invoiceNo)}
                  onToggle={onBillToggle}
                  currency={currency}
                  isMultiSelectMode={true}
                />
              ))}
            </div>

            {/* Late payment penalty */}
            {totalLateFee > 0 && (
              <LatePaymentPenalty
                lateFee={totalLateFee}
                currency={currency}
                isWaived={lateFeesWaived}
                onWaiveToggle={onWaiveLateFee}
              />
            )}

            {/* Multi-bill notice placed here */}
            {isMultiBill && (
              <div className="bg-[#eff5ff] border border-blue-200/60 rounded-lg p-4 text-[12px] text-blue-700 flex items-start gap-3 relative overflow-hidden mt-2">
                <div className="absolute right-0 top-3 bottom-3 w-1.5 bg-yellow-400 rounded-l-md"></div>
                <AlertCircle className="w-[18px] h-[18px] text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="pr-6">
                  <span className="font-bold block mb-1 text-[13px] text-blue-800">
                    Full payment required for multi-bill selection
                  </span>
                  Partial payments are not allowed when paying multiple bills at
                  once. The total amount is automatically calculated and locked.
                  To make a partial payment, select a single bill only.
                </div>
              </div>
            )}

            {/* Partially Paid details */}
            {!isMultiBill && selectedPartiallyPaidBills.length === 1 && (
              <LastPaymentDetailsCard
                currency={currency}
                totalBillAmount={selectedPartiallyPaidBills[0].billAmount || 0}
                lastPaymentAmount={
                  selectedPartiallyPaidBills[0].lastPaymentAmount || 0
                }
                lastPaymentDate={
                  selectedPartiallyPaidBills[0].lastPaymentDate || ""
                }
                currentOutstanding={
                  selectedPartiallyPaidBills[0].outstandingAmount || 0
                }
              />
            )}
          </div>
        );
      }

      case "5":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">
                Pending Service Payments
              </h4>
              <span className="text-sm text-gray-500">
                {servicePayments?.length || 0} Total
              </span>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {servicePayments?.map((servicePayment: any) => (
                <ServicePaymentCard
                  key={servicePayment.id}
                  servicePayment={servicePayment}
                  isSelected={selectedServicePayments.includes(
                    servicePayment.id,
                  )}
                  onToggle={onServicePaymentToggle}
                  currency={currency}
                />
              ))}
            </div>
          </div>
        );

      case "6":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">
                Select Installment to Pay
              </h4>
              <span className="text-sm text-gray-500">
                {installments?.length} Pending
              </span>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {installments?.map((installment, index) => (
                <InstallmentCard
                  key={installment.id}
                  installment={installment}
                  isSelected={selectedInstallments.includes(installment.id)}
                  onToggle={onInstallmentToggle}
                  currency={currency}
                  index={index}
                />
              ))}
            </div>
          </div>
        );

      case "7":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">
                Pending Service Orders
              </h4>
              <span className="text-sm text-gray-500">
                {serviceOrderPayments?.length} Total
              </span>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {serviceOrderPayments?.map((serviceOrder) => (
                <ServiceOrderCard
                  key={serviceOrder.id}
                  serviceOrder={serviceOrder}
                  isSelected={selectedServiceOrders.includes(serviceOrder.id)}
                  onToggle={onServiceOrderToggle}
                  currency={currency}
                />
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mt-2">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-medium">
          2
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Payment Details</h2>
      </div>

      {/* Payment Channel and Type Dropdowns */}
      <DynamicForm
        fields={fields}
        form={form}
        onFieldChange={onFieldChange}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
      />

      {/* Entity Cards */}
      {paymentType && <Card className="mt-6">{renderEntityCards()}</Card>}

      {/* Amount Field (Read-only, auto-populated) */}
      {paymentType &&
        (selectedBills.length > 0 ||
          selectedInstallments.length > 0 ||
          selectedServiceOrders.length > 0 ||
          selectedServicePayments.length > 0) && (
          <div className="mt-6">
            <DynamicForm
              fields={[
                {
                  name: "amount",
                  label: `Amount (${currency})`,
                  type: "decimal",
                  required: true,
                  disabled:
                    paymentType === "2" ? selectedBills.length > 1 : false,
                  showHelperTooltip:
                    paymentType === "2" && selectedBills.length > 1,
                  tooltipText:
                    "Amount is locked when multiple bills are selected",
                  classes: {
                    container: "w-full",
                    label: "block text-sm font-medium text-gray-700 mb-2",
                    input:
                      "w-full h-12 px-4 border border-gray-300 rounded-lg bg-gray-50",
                  },
                },
              ]}
              form={form}
              onFieldChange={onFieldChange}
              config={{
                showSubmitButton: false,
                showResetButton: false,
              }}
            />
          </div>
        )}
    </div>
  );
};
